import { apiFetch } from 'modules/shared/fetch';
import {
  AvsUpdateConfigPayload,
  AvsVerificationData,
  UpgradeType,
} from './avs.types';

const pageSize = 20;

export default class AvsService {
  static async getAvsConfigurations({
    appToken,
    appEnvironment,
  }: {
    appToken: string;
    appEnvironment: string;
  }) {
    const response = await apiFetch('/verifications/v2/iban/banks', {
      // @ts-ignore
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
    });

    return response?.data;
  }

  static async updateAvsConfigurations({
    data,
    appToken,
    appEnvironment,
  }: {
    appToken: string;
    appEnvironment: string;
    data: AvsUpdateConfigPayload[];
  }) {
    const response = await apiFetch('/verifications/v2/iban/banks', {
      // @ts-ignore
      method: 'PUT',
      body: {
        availabilities: data,
      },
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
    });

    return response?.data;
  }

  static async verifyAccount({
    data,
    appToken,
    appEnvironment,
  }: {
    appToken: string;
    appEnvironment: string;
    data: AvsVerificationData;
  }) {
    const response = await apiFetch('/verifications/v2/iban/', {
      // @ts-ignore
      method: 'POST',
      body: data,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
    });

    return response?.data;
  }

  static async uploadBulkVerification({
    data,
    appToken,
    appEnvironment,
  }: {
    appToken: string;
    appEnvironment: string;
    data: any;
  }) {
    const formData = new FormData();
    formData.set('file', data);

    const response = await apiFetch('/verifications/v2/iban/bulk', {
      // @ts-ignore
      method: 'POST',
      body: formData,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
      stringifyBody: false,
      removeContentType: true,
    });

    return response?.data;
  }

  static async getVerificationRecords({
    appToken,
    appEnvironment,
    page,
  }: {
    appToken: string;
    appEnvironment: string;
    page: number;
  }) {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(page - 1));
    searchParams.set('size', String(pageSize));
    const response = await apiFetch(
      `/verifications/v2/iban/records?${searchParams}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
        },
      },
    );

    return response?.data;
  }

  static async getBulkVerificationResult({
    appToken,
    requestId,
    appEnvironment,
  }: {
    appToken: string;
    requestId: string;
    appEnvironment: string;
  }) {
    const response = await apiFetch(
      `/verifications/v2/iban/bulk/${requestId}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
        },
      },
    );

    return response?.data;
  }

  static async getBulkVerificationResultData({
    appToken,
    requestId,
    isCsvFile,
    appEnvironment,
    page,
  }: {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    appEnvironment: string;
    page?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (!isCsvFile) {
      if (page) {
        searchParams.set('page', String(page - 1));
      }
      searchParams.set('size', String(pageSize));
    }
    const response = await apiFetch(
      `/verifications/v2/iban/bulk/${requestId}/verifications?${searchParams.toString()}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
          ...(isCsvFile && { Accept: 'text/csv' }),
        },
      },
    );

    return response?.data;
  }

  static async getIbanVerificationResult({
    appToken,
    requestId,
    isCsvFile,
    appEnvironment,
  }: {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    appEnvironment: string;
  }) {
    const response = await apiFetch(`/verifications/v2/iban/${requestId}`, {
      // @ts-ignore
      method: 'GET',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
        ...(isCsvFile && { Accept: 'text/csv' }),
      },
    });

    return response?.data;
  }

  static async requestServiceAccess({
    appToken,
    appEnvironment,
    upgradeType,
  }: {
    appToken: string;
    appEnvironment: string;
    upgradeType: UpgradeType;
  }) {
    const url = `/notifications/slack?type=${upgradeType}`;
    const response = await apiFetch(url, {
      // @ts-ignore
      method: 'POST',
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
    });

    return response?.data;
  }

  // International Verification Methods
  static async uploadInternationalBulkVerification({
    data,
    appToken,
    appEnvironment,
  }: {
    appToken: string;
    appEnvironment: string;
    data: any;
  }) {
    const formData = new FormData();
    formData.set('file', data);

    const response = await apiFetch('/verifications/v1/accounts/bulk', {
      // @ts-ignore
      method: 'POST',
      body: formData,
      appEnvironment,
      headers: {
        'lean-app-token': appToken,
      },
      stringifyBody: false,
      removeContentType: true,
    });

    return response?.data;
  }

  static async getInternationalVerificationRecords({
    appToken,
    appEnvironment,
    page,
  }: {
    appToken: string;
    appEnvironment: string;
    page: number;
  }) {
    const searchParams = new URLSearchParams();
    searchParams.set('page', String(page - 1));
    searchParams.set('size', String(pageSize));
    const response = await apiFetch(
      `/verifications/v1/accounts/records?${searchParams}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
        },
      },
    );

    return response?.data;
  }

  static async getInternationalBulkVerificationResult({
    appToken,
    requestId,
    appEnvironment,
  }: {
    appToken: string;
    requestId: string;
    appEnvironment: string;
  }) {
    const response = await apiFetch(
      `/verifications/v1/accounts/bulk/${requestId}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
        },
      },
    );

    return response?.data;
  }

  static async getInternationalBulkVerificationResultData({
    appToken,
    requestId,
    isCsvFile,
    appEnvironment,
    page,
  }: {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    appEnvironment: string;
    page?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (!isCsvFile) {
      if (page) {
        searchParams.set('page', String(page - 1));
      }
      searchParams.set('size', String(pageSize));
    }
    const response = await apiFetch(
      `/verifications/v1/accounts/bulk/${requestId}/verifications?${searchParams.toString()}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
          ...(isCsvFile && { Accept: 'text/csv' }),
        },
      },
    );

    return response?.data;
  }

  static async getInternationalVerificationResult({
    appToken,
    requestId,
    isCsvFile,
    appEnvironment,
  }: {
    appToken: string;
    requestId: string;
    isCsvFile?: boolean;
    appEnvironment: string;
  }) {
    const response = await apiFetch(
      `/verifications/v1/accounts/${requestId}`,
      {
        // @ts-ignore
        method: 'GET',
        appEnvironment,
        headers: {
          'lean-app-token': appToken,
          ...(isCsvFile && { Accept: 'text/csv' }),
        },
      },
    );

    return response?.data;
  }
}
